import Splide from '@splidejs/splide';
import { isPageSliderActive } from '../page-slider.js';

const gallerySplides = new Map();
const activeSectionChangeEventName = 'section-keyboard:active-section-change';
const mobileSectionObserverThreshold = 0.5;

const galleryI18n = {
  prev: 'Imagem anterior da galeria',
  next: 'Próxima imagem da galeria',
  first: 'Ir para a primeira imagem',
  last: 'Ir para a última imagem',
  slideX: 'Ir para a imagem %s',
  pageX: 'Ir para a página %s',
  play: 'Iniciar reprodução automática',
  pause: 'Pausar reprodução automática',
  carousel: 'Carrossel de imagens',
  select: 'Selecione uma imagem para exibir',
  slide: 'imagem',
  slideLabel: '%s de %s',
};

function shouldAutoplay() {
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getInitialDesktopActiveSection() {
  if (!isPageSliderActive()) {
    return null;
  }

  return document.querySelector('#page-slider .splide__slide.is-active section');
}

function getViewportVisibilityRatio(element) {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const visibleTop = Math.max(rect.top, 0);
  const visibleBottom = Math.min(rect.bottom, viewportHeight);
  const visibleHeight = Math.max(visibleBottom - visibleTop, 0);

  if (rect.height <= 0) {
    return 0;
  }

  return visibleHeight / rect.height;
}

function isLayoutVisible(layout) {
  if (!(layout instanceof Element)) {
    return true;
  }

  return layout.getClientRects().length > 0 && getComputedStyle(layout).display !== 'none';
}

function shouldMountSection(section, carouselEl) {
  if (!carouselEl || !section.isConnected) {
    return false;
  }

  const layout = section.closest('.layout-desktop, .layout-mobile');

  if (!isLayoutVisible(layout)) {
    return false;
  }

  return true;
}

function mountGallery(section) {
  const carouselEl = section.querySelector('.splide');
  const prevArrowEl = section.querySelector('.splide__arrow--prev');
  const nextArrowEl = section.querySelector('.splide__arrow--next');
  const counterCurrentEl = section.querySelector('.galeria__counter-current');
  const counterTotalEl = section.querySelector('.galeria__counter-total');
  const srAnnouncementEl = section.querySelector('.galeria__sr-only');
  const autoplayToggleEl = section.querySelector('.galeria__autoplay-toggle');

  if (!carouselEl) {
    return;
  }

  const galleryLayout = carouselEl.dataset.galleryLayout || 'desktop';
  const isMobilePairs = galleryLayout === 'mobile-pairs';

  const autoplayIntervalMs = 4000;
  let shouldAnnounceSlide = false;
  let isDragging = false;
  let isDesktopActive = false;
  let isMobileActive = false;
  let isUserPaused = false;
  let sectionObserver = null;
  let viewportActivityFrame = 0;

  function syncAutoplayState() {
    const autoplay = gallery.Components.Autoplay;

    if (!autoplay) {
      return;
    }

    const isSectionActive = isPageSliderActive() ? isDesktopActive : isMobileActive;

    if (!shouldAutoplay() || !isSectionActive || isUserPaused) {
      autoplay.pause();
      return;
    }

    autoplay.play();
  }

  function mountSectionObserver() {
    if (sectionObserver || isPageSliderActive()) {
      return;
    }

    sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target !== section) {
          return;
        }

        isMobileActive = entry.isIntersecting && entry.intersectionRatio > mobileSectionObserverThreshold;
        syncAutoplayState();
        updateAutoplayToggle();
      });
    }, {
      root: null,
      rootMargin: '0px',
      threshold: [0, mobileSectionObserverThreshold, 0.75, 1],
    });

    sectionObserver.observe(section);
  }

  function unmountSectionObserver() {
    if (!sectionObserver) {
      return;
    }

    sectionObserver.disconnect();
    sectionObserver = null;
    isMobileActive = false;
  }

  function updateViewportActivity() {
    isDesktopActive = getInitialDesktopActiveSection() === section;

    if (isPageSliderActive()) {
      unmountSectionObserver();
      syncAutoplayState();
      updateAutoplayToggle();
      return;
    }

    mountSectionObserver();
    isMobileActive = getViewportVisibilityRatio(section) > mobileSectionObserverThreshold;
    syncAutoplayState();
    updateAutoplayToggle();
  }

  function queueViewportActivityUpdate() {
    if (viewportActivityFrame) {
      cancelAnimationFrame(viewportActivityFrame);
    }

    viewportActivityFrame = requestAnimationFrame(() => {
      viewportActivityFrame = 0;
      updateViewportActivity();
    });
  }

  function handleActiveSectionChange(event) {
    const activeSection = event.detail && event.detail.section;
    const viaKeyboard = Boolean(event.detail && event.detail.viaKeyboard);

    isDesktopActive = activeSection === section;
    syncAutoplayState();
    updateAutoplayToggle();

    if (activeSection !== section) {
      return;
    }

    requestAnimationFrame(() => {
      gallery.refresh();

      if (carouselEl.offsetParent !== null) {
        if (viaKeyboard) {
          carouselEl.focus();
        }
      }
    });
  }

  const gallery = new Splide(carouselEl, {
    type: 'loop',
    perPage: 1,
    autoplay: 'pause',
    interval: autoplayIntervalMs,
    resetProgress: false,
    pauseOnHover: false,
    pauseOnFocus: false,
    pagination: false,
    arrows: true,
    keyboard: 'focused',
    i18n: galleryI18n,
  });

  function setAutoplayProgress(value) {
    if (!autoplayToggleEl) {
      return;
    }

    const normalized = Math.min(Math.max(value, 0), 1);
    autoplayToggleEl.style.setProperty('--galeria-autoplay-progress', String(normalized));
  }

  function updateCounter(nextIndex = gallery.index) {
    if (!counterCurrentEl || !counterTotalEl) {
      return;
    }

    const totalSlides = Math.max(gallery.Components.Controller.getEnd() + 1, 1);
    const normalizedIndex = ((nextIndex % totalSlides) + totalSlides) % totalSlides;
    const currentSlide = normalizedIndex + 1;

    counterCurrentEl.textContent = String(currentSlide);
    counterTotalEl.textContent = String(totalSlides);
  }

  function announceCurrentSlide(index = gallery.index) {
    if (!srAnnouncementEl) {
      return;
    }

    const totalSlides = Math.max(gallery.Components.Controller.getEnd() + 1, 1);
    const normalizedIndex = ((index % totalSlides) + totalSlides) % totalSlides;
    const currentSlide = normalizedIndex + 1;

    srAnnouncementEl.textContent = isMobilePairs
      ? `Slide ${currentSlide} de ${totalSlides}`
      : `Imagem ${currentSlide} de ${totalSlides}`;
  }

  function updateAutoplayToggle() {
    if (!autoplayToggleEl) {
      return;
    }

    const autoplay = gallery.Components.Autoplay;
    const isPlaying = autoplay ? !autoplay.isPaused() : false;

    autoplayToggleEl.classList.toggle('is-playing', isPlaying);
    autoplayToggleEl.setAttribute('aria-pressed', String(isPlaying));
    autoplayToggleEl.setAttribute(
      'aria-label',
      isPlaying
        ? 'Pausar reprodução automática da galeria'
        : 'Iniciar reprodução automática da galeria'
    );
  }

  if (autoplayToggleEl) {
    autoplayToggleEl.onclick = () => {
      const autoplay = gallery.Components.Autoplay;

      if (!autoplay) {
        return;
      }

      if (autoplay.isPaused()) {
        isUserPaused = false;
      } else {
        isUserPaused = true;
      }

      syncAutoplayState();
      updateAutoplayToggle();
    };
  }

  carouselEl.onclick = (event) => {
    if (isDragging) {
      return;
    }

    const clickedControl = event.target.closest(
      '.splide__arrow, .galeria__autoplay-toggle, a, button'
    );

    if (clickedControl) {
      return;
    }

    const autoplay = gallery.Components.Autoplay;

    if (!autoplay) {
      return;
    }

    if (autoplay.isPaused()) {
      isUserPaused = false;
    } else {
      isUserPaused = true;
    }

    syncAutoplayState();
    updateAutoplayToggle();
  };

  if (prevArrowEl) {
    prevArrowEl.onclick = () => {
      shouldAnnounceSlide = true;
    };
  }

  if (nextArrowEl) {
    nextArrowEl.onclick = () => {
      shouldAnnounceSlide = true;
    };
  }

  carouselEl.onkeydown = (event) => {
    const target = event.target;
    const isEditableField = target.closest('input, textarea, select');

    if (isEditableField) {
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      event.stopPropagation();
      shouldAnnounceSlide = true;
      gallery.go('<');
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      event.stopPropagation();
      shouldAnnounceSlide = true;
      gallery.go('>');
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      event.stopPropagation();
      shouldAnnounceSlide = true;
      gallery.go(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      event.stopPropagation();
      shouldAnnounceSlide = true;
      gallery.go(gallery.Components.Controller.getEnd());
      return;
    }

    if (event.key !== ' ' && event.key !== 'Spacebar') {
      return;
    }

    if (target.closest('.galeria__autoplay-toggle')) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const autoplay = gallery.Components.Autoplay;

    if (!autoplay) {
      return;
    }

    if (autoplay.isPaused()) {
      isUserPaused = false;
    } else {
      isUserPaused = true;
    }

    syncAutoplayState();
    updateAutoplayToggle();
  };

  gallery.on('move', (nextIndex) => {
    updateCounter(nextIndex);
  });
  gallery.on('drag', () => {
    isDragging = true;
    shouldAnnounceSlide = true;
    section.classList.add('galeria--dragging');
  });
  gallery.on('dragged', () => {
    requestAnimationFrame(() => {
      isDragging = false;
      section.classList.remove('galeria--dragging');
    });
  });
  gallery.on('moved', () => {
    setAutoplayProgress(0);
  });
  gallery.on('moved', (newIndex) => {
    if (!shouldAnnounceSlide) {
      return;
    }

    announceCurrentSlide(newIndex);
    shouldAnnounceSlide = false;
  });
  gallery.on('mounted moved updated refresh', updateCounter);
  gallery.on('autoplay:playing', (rate) => {
    if (typeof rate === 'number') {
      setAutoplayProgress(rate);
      return;
    }

    if (rate && typeof rate.rate === 'number') {
      setAutoplayProgress(rate.rate);
    }
  });
  gallery.on('mounted autoplay:play autoplay:pause', updateAutoplayToggle);
  gallery.on('destroy', () => {
    if (viewportActivityFrame) {
      cancelAnimationFrame(viewportActivityFrame);
    }

    const autoplay = gallery.Components.Autoplay;

    if (autoplay) {
      autoplay.pause();
    }

    unmountSectionObserver();
    section.classList.remove('galeria--dragging');
    document.removeEventListener(activeSectionChangeEventName, handleActiveSectionChange);
    window.removeEventListener('resize', queueViewportActivityUpdate);
    window.removeEventListener('scroll', queueViewportActivityUpdate);
  });

  gallery.mount();
  document.addEventListener(activeSectionChangeEventName, handleActiveSectionChange);
  window.addEventListener('resize', queueViewportActivityUpdate);
  window.addEventListener('scroll', queueViewportActivityUpdate, {
    passive: true,
  });
  setAutoplayProgress(0);
  updateCounter();
  updateViewportActivity();
  updateAutoplayToggle();
  gallerySplides.set(section, gallery);
}

export function syncGalleries() {
  const sections = document.querySelectorAll('.galeria');

  sections.forEach((section) => {
    const carouselEl = section.querySelector('.splide');
    const isVisible = shouldMountSection(section, carouselEl);
    const instance = gallerySplides.get(section);

    if (isVisible && !instance) {
      mountGallery(section);
      return;
    }

    if (!isVisible && instance) {
      instance.destroy(true);
      gallerySplides.delete(section);
      return;
    }

    if (isVisible && instance) {
      instance.refresh();
    }
  });
}
